import { useEffect, useState, useCallback } from "react";
import { appConfirm } from "../../components/ConfirmDialog";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, RotateCcw, XCircle } from "lucide-react";

const gbp = (n) => "£" + Math.round(Number(n || 0)).toLocaleString("en-GB");

export function TrashPanel({ onOpenBooking }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/admin/trash"); setRows(r.data.items || []); }
    catch { toast.error("Could not load Trash"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (ref) => {
    try { await api.post(`/admin/trash/${ref}/restore`); toast.success("Restored"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not restore"); }
  };
  const purge = async (ref) => {
    if (!(await appConfirm({ title: "Permanently delete this job file?", description: "This cannot be undone.", confirmLabel: "Delete forever", destructive: true }))) return;
    try { await api.delete(`/admin/trash/${ref}`); toast.success("Permanently deleted"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div data-testid="trash-panel">
      <div className="flex items-center gap-2 mb-1"><Trash2 size={18} className="text-[#C0392B]" /><h2 className="text-2xl font-black text-[#12263A]">Deleted job files</h2></div>
      <p className="text-sm text-[#6B7280] mb-5">Soft-deleted customers. Restore to bring a job file back, or permanently delete it.</p>
      {loading ? <div className="py-10 text-center text-sm text-[#6B7280]">Loading…</div> : rows.length === 0 ? (
        <div className="py-10 text-center text-sm text-[#9CA3AF] border border-dashed border-[#E5E7EB]">Trash is empty.</div>
      ) : (
        <div className="border border-[#E5E7EB] bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#F8F6F2] text-left text-[11px] uppercase tracking-widest text-[#6B7280]">
              <th className="px-3 py-2.5">Client</th><th className="px-3 py-2.5">Ref</th><th className="px-3 py-2.5">Contact</th>
              <th className="px-3 py-2.5 text-right">Quoted</th><th className="px-3 py-2.5">Deleted</th><th className="px-3 py-2.5"></th>
            </tr></thead>
            <tbody>
              {rows.map((c, i) => (
                <tr key={c.reference || i} data-testid={`trash-row-${c.reference}`} className="border-t border-[#E5E7EB]">
                  <td className="px-3 py-2.5 font-bold text-[#12263A]">{c.name || "—"}</td>
                  <td className="px-3 py-2.5 font-mono text-[#6B7280]">{c.reference}</td>
                  <td className="px-3 py-2.5 text-[#6B7280]">{c.email || "—"}</td>
                  <td className="px-3 py-2.5 text-right text-[#6B7280]">{gbp(c.total_inc_vat)}</td>
                  <td className="px-3 py-2.5 text-[#6B7280] text-xs">{(c.deleted_at || "").slice(0, 10)}<br />{c.deleted_by}</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button data-testid={`trash-restore-${c.reference}`} onClick={() => restore(c.reference)}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1F6B3B] border border-[#CDE7D6] px-2.5 py-1 hover:border-[#1F6B3B] mr-1.5"><RotateCcw size={13} /> Restore</button>
                    <button data-testid={`trash-purge-${c.reference}`} onClick={() => purge(c.reference)}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#C0392B] border border-[#F1D4CE] px-2.5 py-1 hover:border-[#C0392B]"><XCircle size={13} /> Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
